import { ViewBooking } from "collins-js-sdk";
import React, { useState } from "react";
import { useAppContext } from "../../../../../hooks/useAppContext";
import { DietaryRequirement } from "../../../../../types/bookings";
import { EDSButton } from "../../../../eds/EDSButton";
import { Input } from "../../../../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../../../ui/select";
import DietaryRequirementsModal from "../../SelectedBooking/PreOrders/DietaryRequirementsModal";
import FindCustomerModal from "./FindCustomerModal";

type AddBookingStep2Type = {
  booking: ViewBooking;
  updateBooking: (bookingProps: Partial<ViewBooking>) => void;
};
const AddBookingStep2 = ({ booking, updateBooking }: AddBookingStep2Type) => {
  const { venueGroupConfig } = useAppContext();
  const [viewCustomers, setViewCustomers] = useState<boolean>(false);
  const [isDietaryModalOpen, setIsDietaryModalOpen] = useState(false);
  const [selectedDietaryRequirement, setSelectedDietaryRequirement] = useState<
    DietaryRequirement | undefined
  >();
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>();

  const dietaryRequirements = booking.dietaryRequirements ?? [];

  const handleAddDietaryRequirement = () => {
    setSelectedDietaryRequirement(undefined);
    setSelectedIndex(undefined);
    setIsDietaryModalOpen(true);
  };

  const handleEditDietaryRequirement = (
    requirement: DietaryRequirement,
    index: number
  ) => {
    setSelectedDietaryRequirement(requirement);
    setSelectedIndex(index);
    setIsDietaryModalOpen(true);
  };

  const handleDeleteDietaryRequirement = (index: number) => {
    const updatedRequirements = [...dietaryRequirements];
    updatedRequirements.splice(index, 1);
    updateBooking({ dietaryRequirements: updatedRequirements });
  };

  const handleSaveDietaryRequirement = (requirement: DietaryRequirement) => {
    const updatedRequirements = [...dietaryRequirements];

    if (selectedIndex !== undefined) {
      updatedRequirements[selectedIndex] = requirement;
    } else {
      updatedRequirements.push(requirement);
    }

    updateBooking({ dietaryRequirements: updatedRequirements });
    setIsDietaryModalOpen(false);
  };

  const formatDietaryRequirements = (
    requirement: DietaryRequirement
  ): string => {
    const items = [
      ...(requirement.diet_types ?? []),
      ...(requirement.allergies ?? [])
    ];
    return items.length > 0 ? items.join(", ") : "No requirements specified";
  };

  return (
    <div className="flex flex-col flex-1 h-full overflow-x-visible overflow-y-hidden">
      <div className="mb-0.5 py-2.5 font-normal [&_button]:font-normal">
        <EDSButton
          variant="default"
          className="w-full"
          onClick={() => setViewCustomers(true)}
        >
          Find Customer
        </EDSButton>
      </div>
      <div className="overflow-x-visible overflow-y-auto h-0 flex-grow px-1">
        <div className="w-full flex items-center justify-between border-b border-border py-2.5 px-1 min-w-0 box-border">
          <p className="text-left font-bold text-[var(--formLabelFontSize)] text-foreground m-0 py-4 min-w-[120px] shrink-0 whitespace-nowrap">
            First Name *
          </p>
          <Input
            name="FirstName"
            value={booking.firstName || ""}
            type="text"
            placeholder="Enter First Name"
            className="text-left font-normal text-sm text-foreground bg-transparent border-none p-0 focus:outline-2 focus:outline-ring focus:outline-offset-2 disabled:text-foreground disabled:opacity-50 w-4/5"
            autoComplete="off"
            onChange={(e) =>
              updateBooking({
                firstName: e.target.value
              })
            }
          />
        </div>
        <div className="w-full flex items-center justify-between border-b border-border py-2.5 px-1 min-w-0 box-border">
          <p className="text-left font-bold text-[var(--formLabelFontSize)] text-foreground m-0 py-4 min-w-[120px] shrink-0 whitespace-nowrap">
            Last Name
          </p>
          <Input
            name="LastName"
            value={booking.lastName || ""}
            type="text"
            placeholder="Enter Last Name"
            className="text-left font-normal text-sm text-foreground bg-transparent border-none p-0 focus:outline-2 focus:outline-ring focus:outline-offset-2 disabled:text-foreground disabled:opacity-50 w-4/5"
            autoComplete="off"
            onChange={(e) =>
              updateBooking({
                lastName: e.target.value
              })
            }
          />
        </div>
        <div className="w-full flex items-center justify-between border-b border-border py-2.5 px-1 min-w-0 box-border">
          <p className="text-left font-bold text-[var(--formLabelFontSize)] text-foreground m-0 py-4 min-w-[120px] shrink-0 whitespace-nowrap">
            Email{" "}
          </p>
          <Input
            name="Email"
            value={booking.email || ""}
            type="email"
            className="text-left font-normal text-sm text-foreground bg-transparent border-none p-0 focus:outline-2 focus:outline-ring focus:outline-offset-2 disabled:text-foreground disabled:opacity-50 w-4/5"
            autoComplete="off"
            onChange={(e) =>
              updateBooking({
                email: e.target.value
              })
            }
          />
        </div>
        <div className="w-full flex items-center justify-between border-b border-border py-2.5 px-1 min-w-0 box-border">
          <p className="text-left font-bold text-[var(--formLabelFontSize)] text-foreground m-0 py-4 min-w-[120px] shrink-0 whitespace-nowrap">
            Phone{" "}
          </p>
          <Input
            name="Phone"
            value={booking.phone || ""}
            type="tel"
            className="text-left font-normal text-sm text-foreground bg-transparent border-none p-0 focus:outline-2 focus:outline-ring focus:outline-offset-2 disabled:text-foreground disabled:opacity-50 w-4/5"
            autoComplete="off"
            onChange={(e) =>
              updateBooking({
                phone: e.target.value
              })
            }
          />
        </div>
        {!booking.waitlisted && (
          <>
            <div className="w-full flex items-center justify-between border-b border-border py-2.5 px-1 min-w-0 box-border">
              <p className="text-left font-bold text-[var(--formLabelFontSize)] text-foreground m-0 py-4 min-w-[120px] shrink-0 whitespace-nowrap">
                Company{" "}
              </p>
              <Input
                name="Company"
                value={booking.company || ""}
                type="text"
                className="text-left font-normal text-sm text-foreground bg-transparent border-none p-0 focus:outline-2 focus:outline-ring focus:outline-offset-2 disabled:text-foreground disabled:opacity-50 w-4/5"
                autoComplete="off"
                onChange={(e) =>
                  updateBooking({
                    company: e.target.value
                  })
                }
              />
            </div>
            <div className="w-full flex items-center justify-between border-b border-border py-2.5 px-1 min-w-0 box-border">
              <p className="text-left font-bold text-[var(--formLabelFontSize)] text-foreground m-0 py-4 min-w-[120px] shrink-0 whitespace-nowrap">
                Source{" "}
              </p>
              <div className="w-4/5 [&_input]:w-full">
                <Select
                  value={booking.partnerSource ?? ""}
                  onValueChange={(value) => {
                    if (booking.partnerSource !== value) {
                      updateBooking({
                        partnerSource: value
                      });
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a source" />
                  </SelectTrigger>
                  <SelectContent>
                    {venueGroupConfig?.booking_sources?.map((source) => (
                      <SelectItem key={source.label} value={source.label}>
                        {source.label}
                      </SelectItem>
                    )) ?? []}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </>
        )}
        {!booking.waitlisted && (
          <>
            <div className="w-full flex items-center justify-between border-b border-border py-2.5 px-1 min-w-0 box-border flex-row">
              <p className="text-left font-bold text-[var(--formLabelFontSize)] text-foreground m-0 py-4 min-w-[120px] shrink-0 whitespace-nowrap">
                Dietary Requirements
              </p>
              <EDSButton
                variant="outline"
                size="sm"
                onClick={handleAddDietaryRequirement}
              >
                + Add
              </EDSButton>
            </div>
            <div className="py-2 border-b border-border">
              {dietaryRequirements.length > 0 ? (
                <div className="flex flex-col gap-2 w-full">
                  {dietaryRequirements.map((req, index) => (
                    <div
                      key={`${req.name}-${(req.diet_types ?? []).join(",")}-${(req.allergies ?? []).join(",")}`}
                      className="flex justify-between items-center p-3 border border-[var(--borderColor)] rounded bg-background gap-2"
                    >
                      <div className="flex flex-col gap-1 flex-1 min-w-0">
                        <strong className="font-semibold text-sm">
                          {req.name}
                        </strong>
                        <span className="text-muted-foreground text-[0.8125rem] overflow-hidden text-ellipsis whitespace-nowrap">
                          {formatDietaryRequirements(req)}
                        </span>
                      </div>
                      <div className="flex gap-1.5 shrink-0">
                        <button
                          type="button"
                          className="py-1 px-2 border border-[var(--borderColor)] rounded bg-background cursor-pointer text-xs transition-all duration-150 text-primary hover:bg-primary/10"
                          onClick={() =>
                            handleEditDietaryRequirement(req, index)
                          }
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="py-1 px-2 border border-[var(--borderColor)] rounded bg-background cursor-pointer text-xs transition-all duration-150 text-destructive hover:bg-destructive/10"
                          onClick={() => handleDeleteDietaryRequirement(index)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm italic m-0 py-2">
                  No dietary requirements added
                </p>
              )}
            </div>
          </>
        )}

        <FindCustomerModal
          open={viewCustomers}
          selectedCustomer={(e) => {
            const customer = {
              firstName: e.firstName,
              lastName: e.lastName,
              email: e.email,
              phone: e.phone,
              company: e.company
            };
            updateBooking(customer);
          }}
          handleClose={() => setViewCustomers(false)}
        />

        <DietaryRequirementsModal
          open={isDietaryModalOpen}
          onOpenChange={setIsDietaryModalOpen}
          dietaryRequirement={selectedDietaryRequirement}
          onSave={handleSaveDietaryRequirement}
        />
      </div>
    </div>
  );
};

export default React.memo<AddBookingStep2Type>(AddBookingStep2);
